"use client"

import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useEffect, useState } from "react"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"

export function FeaturedBrands({ hideHeading = false, hideViewAll = false }: { hideHeading?: boolean, hideViewAll?: boolean }) {
  const [brands, setBrands] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchBrands() {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch("/api/brands")
        if (!res.ok) throw new Error("Failed to fetch brands")
        const data = await res.json()
        // Only show active brands and limit to 6 for featured section
        const activeBrands = data.filter((brand: any) => brand.active).slice(0, 6)
        setBrands(activeBrands)
      } catch (err: any) {
        setError(err.message || "Unknown error")
      } finally {
        setLoading(false)
      }
    }
    fetchBrands()
  }, [])

  if (loading) {
    return (
      <section className="py-16 lg:py-20 xl:py-24">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Shop by Brand</h2>
          <p className="text-lg lg:text-xl xl:text-2xl text-muted-foreground mb-8 lg:mb-12">Loading brands...</p>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-16 lg:py-20 xl:py-24">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Shop by Brand</h2>
          <p className="text-lg lg:text-xl xl:text-2xl text-red-500 mb-8 lg:mb-12">{error}</p>
        </div>
      </section>
    )
  }

  return (
    <section className="py-4 md:py-6 bg-muted">
      <div className="w-full max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
        {!hideHeading && (
          <div className="text-center mb-12 lg:mb-16 xl:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Shop by Brand</h2>
            <p className="text-lg lg:text-xl xl:text-2xl text-muted-foreground max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
              Discover products from your favorite brands
            </p>
          </div>
        )}
        <div className="flex gap-2 md:gap-3 lg:gap-4 xl:gap-5 overflow-x-auto pb-2 scrollbar-hide justify-start md:justify-center">
          {brands.map((brand) => (
            <Link key={brand.slug} href={`/brand/${brand.slug}`} className="flex-shrink-0">
              <Card className="group hover:shadow-lg transition-all duration-slow hover:-translate-y-1 h-full w-48 md:w-56 lg:w-64 xl:w-72">
                <CardContent className="p-4 lg:p-6 xl:p-8 text-center h-full flex flex-col">
                  <div className="relative mb-4 lg:mb-6 xl:mb-8 overflow-hidden rounded-lg flex-shrink-0 aspect-square">
                    <ImageWithFallback
                      src={brand.image}
                      alt={brand.name}
                      width={300}
                      height={300}
                      fallbackSrc="/placeholder.svg"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-slow"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <h3 className="font-semibold mb-1 lg:mb-2 text-sm lg:text-base xl:text-lg">{brand.name}</h3>
                    <p className="text-xs lg:text-sm xl:text-base text-muted-foreground">
                      {brand._count?.products || 0} items
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        {!hideViewAll && (
          <div className="text-center mt-12 lg:mt-16 xl:mt-20">
            <Link href="/brands">
              <Button size="lg" variant="outline" className="text-lg px-8 py-3">
                View All Brands
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  )
} 